'use strict';

import jQuery from 'jquery';
import VideoModel from './VideoModel.js';
import timerTemplate from './views/upNextTimer.hbs';
import endScreenTemplate from './views/coveEndscreen.hbs';
import Core from './../../Core/Core';
import ga from './../../../vendor/google-analytics';

/**
 * COVE video player endscreen
 */
var COVEVideoPlayerEndScreen = (function($) {
  return function COVEVideoPlayerEndScreen(options) {

    // Namespace for private variables and functions
    var privateNS = {
      defaults: {
        autoplayDelay: 10000,
        tickFrequency: 1000
      }
    };

    // Namespace for public variables and functions
    var publicNS = {};

    privateNS.init = function init() {

      // Initialize options
      privateNS.options = $.extend({}, privateNS.defaults, options);

      // Create a reference to the container
      privateNS.$container = $(privateNS.options.container);

      // Shortcut to the model
      privateNS.model = privateNS.options.model;

      // Add additional properties to the video for rendering if a video was
      // passed in
      if (privateNS.model &&
          privateNS.model.getContent) {

        // Get the details about this video
        privateNS.content = privateNS.model.getContent();

        privateNS.showName = window.location.pathname.match(/\/(.*?)\//)[1];

        // Compute the tracking label
        privateNS.trackingLabel = [
          'show/' + privateNS.showName + '/video/' + privateNS.content.slug + '/',
          privateNS.content.tp_media_id
        ].join(' | ');

        // Generate the appropriate sizing information for the responsive
        // Cove thumbnail
        privateNS.content.responsive_image = {
          url: privateNS.content.mezzanine,
          image_sizes: [400, 300, 200, 100].map(
            function mapWidthToDimensions(width) {

              // Return the 16:9 image dimensions
              return {
                width: width,
                height: Math.floor(width * 0.5625)
              };
            }
          ),
          sizes: [
            {breakpoint: '1201px', width: '400px'},
            {breakpoint: '769px', width: '33vw'},
            {breakpoint: '641px', width: '50vw'},
            {breakpoint: '0px', width: '100vw'}
          ],
          alt: privateNS.content.title
        };

        // Determine if this item is available for playback for the current user
        privateNS.content.is_playable = privateNS.content.minimum_access_level <= Core.user.getAccessLevel();

        // Mark the video as not playing, given that this is a preview
        privateNS.content.is_playing = false;

        // Flag the videos that are full episodes
        privateNS.content.is_full_episode = privateNS.content.display_type === 'Episode';
      }

      // Add bindings to the future UI
      privateNS.attachHandlers();

      // Define the initial state of the timer
      publicNS.stopAutoplayTimer();
      publicNS.resetAutoplayTimer();

      return publicNS;
    };

    /**
     * Handle the tick of the countdown. On each tick re-render the timer and
     * in the case that the timer runs out, trigger the next video
     */
    privateNS.tick = function tick() {

      // Reduce the timer by the tick frequency
      privateNS.countdownTimer -= privateNS.options.tickFrequency;

      // If the timer has not yet run out, render the new timer
      if (privateNS.countdownTimer >= 0) {
        privateNS.renderTimer();
      } else {

        // Make sure that the timer stops before doing anything else
        publicNS.stopAutoplayTimer();

        // Track when the autoplay timer finishes its countdown
        ga(
          'send',
          'event',
          'VideoPlayer',
          'Endscreen autoplay timer triggered',
          privateNS.trackingLabel,
          {
            transport: 'beacon'
          }
        );

        // Navigate the user to the next video
        privateNS.playVideo();
      }
    };

    /**
     * Handle updates to the timer display. Displays the remaining time in seconds
     */
    privateNS.renderTimer = function renderTimer() {

      privateNS.$container.find('.share-overlay-up-next').html(
        timerTemplate({
          remaining_time: Math.floor(privateNS.countdownTimer / 1000),
          try_autoplay: !$.browser.mobile,
          video_title: privateNS.content.title,
          video_slug: privateNS.content.slug,
          show_slug: privateNS.showName,
        })
      );
    };

    /**
     * Handles requests from the user to immediately play the upcoming video.
     *
     * @param {Event} event
     */
    privateNS.userRequestedPlay = function userRequestedPlay(event) {

      event.preventDefault();

      // Track when the user chooses to immediately play a video
      ga(
        'send',
        'event',
        'VideoPlayer',
        'Endscreen play trigger by user',
        privateNS.trackingLabel,
        {
          transport: 'beacon'
        }
      );

      privateNS.playVideo();
    };

    /**
     * Handles the mechanism of transitioning to the upcoming video
     */
    privateNS.playVideo = function playVideo() {

      // Navigate the user to the next video
      privateNS.options.navigator.changeState(privateNS.content.id);
    };

    /**
     * Handles requests from the user to stop autoplay timer.
     *
     * @param {Event} event
     */
    privateNS.userRequestedCancel = function userRequestedCancel(event) {

      event.preventDefault();

      // Track when the user chooses to cancel the autoplay
      ga(
        'send',
        'event',
        'VideoPlayer',
        'Endscreen autoplay cancelled',
        privateNS.trackingLabel,
        {
          transport: 'beacon'
        }
      );

      publicNS.stopAutoplayTimer();
    };

    /**
     * Binds events handlers for user interactions with the endscreen
     */
    privateNS.attachHandlers = function attachHandlers() {

      // Handle immediate clicks on the play button
      privateNS.$container.on(
        'click',
        '.video-item-watchable, .up-next-mobile-title',
        privateNS.userRequestedPlay
      );

      // Handle clicks on the cancel autoplay option
      privateNS.$container.on(
        'click',
        '.autoplay-cancel',
        privateNS.userRequestedCancel
      );
    };

    /**
     * Renders the endscreen to the provided container
     */
    publicNS.render = function render() {

      // Track when the autoplay endscreen is displayed to the user
      ga(
        'send',
        'event',
        'VideoPlayer',
        'Endscreen displayed',
        privateNS.trackingLabel,
        {
          transport: 'beacon'
        }
      );

      // Render the endscreen and add the required additional classes to the
      // video UI element
      privateNS.$container.html(
        endScreenTemplate({
          remaining_time: Math.floor(privateNS.countdownTimer / 1000),
          content: privateNS.content,
          show: {
            post_name: privateNS.showName
          },
          try_autoplay: !$.browser.mobile,
          user: {
            uid: Core.user.getUid(),
            access_level: Core.user.getAccessLevel(),
            is_signed_in: Core.user.isSignedIn(),
            is_expired: Core.user.isExpired()
          }
        })
      );

      // Get a reference to the watchable
      var $watchable = privateNS.$container.find('.video-item-watchable');

      // Make the watchable visible
      $watchable.removeClass('faded');

      // If the device is not a mobile device, trigger autoplaying
      $watchable.find('.autoplay-timer').addClass('animated');
    };

    /**
     * Handle resetting the timer to its full duration
     */
    publicNS.resetAutoplayTimer = function resetAutoplayTimer() {

      // Create a counter to store the state of the autoplay timer
      privateNS.countdownTimer = privateNS.options.autoplayDelay;
    };

    /**
     * Schedule the timer that will run the tick at the defined frequency
     */
    publicNS.startAutoplayTimer = function startAutoplayTimer() {

      privateNS.timerId = setInterval(
        privateNS.tick,
        privateNS.options.tickFrequency
      );
    };

    /**
     * Stop the autoplay time and update the UI to reflect it
     */
    publicNS.stopAutoplayTimer = function stopAutoplayTimer() {

      clearInterval(privateNS.timerId);
      privateNS.timerId = null;
      privateNS.$container.find('.autoplay-timer').removeClass('animated');
      privateNS.$container.find('.up-next-timer').addClass('hide');
      privateNS.$container.find('.autoplay-cancel').html('&nbsp;');
      publicNS.resetAutoplayTimer();
    };

    /**
     * Destroy the internal state of the endscreen
     */
    publicNS.destroy = function destroy() {

      if (privateNS && publicNS) {
        publicNS.stopAutoplayTimer();
        privateNS = null;
        publicNS = null;
      }
    };

    return privateNS.init();
  };
})(jQuery);

export default COVEVideoPlayerEndScreen